import { useState, useRef, useCallback, useEffect } from 'react';
import { AxiosResponse } from 'axios';
import useApiFetch from '@/hooks/useApiFetch';
import useFocus from '@/hooks/useFocus';
import useOnClickOutside from '@/hooks/useOnClickOutside';
import candidatesServices, { Candidate } from '@/services/candidates';
import { FetchApiIds } from '@/types';
import CandidateSearchComponent from './CandidateSearch.component';

const CandidateSearch = (props): JSX.Element => {
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const isInputSearchFocused = useFocus(inputRef);

  const [isCandidateSearchListVisible, setCandidateSearchListVisible] = useState<boolean>(true);
  const [wordToSearch, setWordToSearch] = useState<string>('');

  useEffect(() => {
    if (isInputSearchFocused) setCandidateSearchListVisible(true);
  }, [isInputSearchFocused]);

  const hideCandidateSearchList = useCallback((event: any) => {
    const eventPath = event.composedPath() ?? [];
    const componentIDs = ['candidate-search-input', 'candidate-search-list'];
    const checkPathId = (path): boolean => componentIDs.includes(path.id);
    const hasClickedInsideComponent = eventPath.some(checkPathId);

    if (!hasClickedInsideComponent) {
      setCandidateSearchListVisible(false);
    }
  }, []);

  useOnClickOutside(containerRef, hideCandidateSearchList);

  const candidateSearchResponse = useApiFetch<Candidate[]>(
    [FetchApiIds.CANDIDATE_SEARCH, wordToSearch],
    async () => {
      const [nameResponse, skillResponse] = await Promise.all([
        candidatesServices.searchCandidate(wordToSearch),
        candidatesServices.searchCandidatesBySkill(wordToSearch),
      ]);

      const nameCandidates = nameResponse.data ?? [];
      const skillCandidates = skillResponse.data ?? [];

      const candidateMap = new Map<string, Candidate>();
      [...nameCandidates, ...skillCandidates].forEach((candidate) => {
        if (!candidateMap.has(candidate.id)) {
          candidateMap.set(candidate.id, candidate);
        }
      });

      return { data: Array.from(candidateMap.values()) } as AxiosResponse<Candidate[]>;
    },
    { enabled: wordToSearch.length >= 3 }
  );

  const onChangeHandler = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const {
      target: { value },
    } = event;

    setWordToSearch(value);
  };

  return (
    <CandidateSearchComponent
      {...props}
      isLoading={candidateSearchResponse.isLoading}
      hasError={candidateSearchResponse.isError}
      hasFetched={candidateSearchResponse.isFetched}
      candidates={candidateSearchResponse.data ?? []}
      onChangeHandler={onChangeHandler}
      value={wordToSearch}
      containerRef={containerRef}
      ref={inputRef}
      isCandidateSearchListVisible={isCandidateSearchListVisible}
    />
  );
};

export default CandidateSearch;
